// Raising, and what happens in the seconds after it. Lifted from the Andon
// app's `useRaise`, with one step in front of it that the Andon app did not
// need.
//
// One place, because two would drift, and because the sequence matters more
// than any single call in it:
//
//   0  the station the device holds is sealed into the session, if it is not
//      already
//   1  the alert row is created, the SLA clock starts, tier 1 is notified,
//      and the server posts the raiser's opening bubble
//   2  the photos upload and land ON that bubble
//   3  the screen lands on the andon
//
// STEP 0 IS THE SHOP FLOOR'S. The Andon app asked for the station at the door
// and sealed it once; the shop floor keeps the station on the device and tells
// the server whenever it changes (see Shell), but that is one request that can
// fail without anybody noticing — the module was dormant when it was sent, the
// network dropped, the cookie lapsed overnight on a kiosk. The raise is the one
// moment the seal has to have landed, so it is checked here against what home
// said the session holds, and sealed again only when the two disagree. A
// device with no station at all seals nothing and lets the server refuse in
// its own words. See `stationSealNeeded`.
//
// STEP 2 IS NOT PART OF STEP 1, DELIBERATELY. A photo is 300kB. Putting it
// inside the raise call means the notification waits on the upload — which on
// a factory network is exactly when it is slowest and exactly when somebody is
// standing next to a stopped line. So the alert goes first, with the answers on
// it, and the photos follow into the thread within a second or two. A failed
// upload costs a photo, never the alert.
//
// The answers are different and DO ride with the raise: they are what choose
// the responder, so an alert created without them is routed by a coin flip.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AlertTypeKey } from '@/api/andon/types';
import { useT } from '@/i18n/useT';
import { raiseLanding, stationSealNeeded, type StationRef } from '@/lib/raiseFlow';
import { andonKeys } from '@/queries/andon';
import { useWriteError } from '@/queries';
import { useStore } from '@/store';
import type { DraftAttachment } from '@/store/slices/andon';

export function useRaise() {
  const t = useT();
  const qc = useQueryClient();
  const onError = useWriteError();
  const setTab = useStore((s) => s.setTab);
  const openAndon = useStore((s) => s.openAndon);
  const resetDraft = useStore((s) => s.resetDraft);
  const showError = useStore((s) => s.showError);

  return useMutation({
    mutationFn: async (input: {
      type: AlertTypeKey;
      /**
       * The card's own list, which wins over the store's: the last chip and
       * the send can happen in the same tick, and a state update scheduled
       * behind it would send the register as it was one question ago.
       */
      answers: Array<{ question: string; answer: string }>;
      attachments: DraftAttachment[];
      /** What home said the session holds. Undefined when home never landed. */
      sealed: StationRef | null | undefined;
    }) => {
      // Read at send time, not at render: the draft is the store's and the
      // store is the truth about what was typed a moment ago.
      const s = useStore.getState();
      const device: StationRef | null =
        s.workcentreId != null ? { workcentreId: s.workcentreId, flowlineId: s.flowlineId } : null;

      if (stationSealNeeded(device, input.sealed) === 'seal' && device) {
        await andonApi.setStation(device.workcentreId, device.flowlineId);
      }

      const { alert, raiseMessageId } = await andonApi.raise({
        stars: s.draftStars,
        type: input.type,
        productionId: s.draftPodId,
        words: s.draftWords,
        answers: input.answers,
      });

      // Uploaded as a group rather than one at a time, so three photos cost
      // one round trip's latency rather than three.
      const landing = raiseLanding(alert);
      if (input.attachments.length && landing.kind === 'alert') {
        const landed = await Promise.allSettled(
          input.attachments.map((f) =>
            andonApi.attach({ alertId: landing.id, kind: f.kind, data: f.data, name: f.name }),
          ),
        );
        /*
         * ONTO THE RAISE BUBBLE, so the raise is one thing somebody said.
         *
         * The bubble went up before these did, deliberately — see the header.
         * The FALLBACK matters: `raiseMessageId` is null on an instance whose
         * addon predates the message log, and there the files stay on the
         * alert with no bubble to join, exactly as they did before. Losing the
         * grouping is a cosmetic regression; refusing to attach would lose the
         * photo of the empty rack.
         */
        const ids = landed.flatMap((l) =>
          l.status === 'fulfilled' && Number.isInteger(l.value?.attachment?.id) ? [l.value.attachment.id] : [],
        );
        if (raiseMessageId != null && ids.length) {
          // Best-effort: the files are already on the andon and reachable from
          // its attachment list. What fails here is the grouping, not the
          // evidence.
          await andonApi.attachToMessage(landing.id, raiseMessageId, ids).catch(() => {});
        }
      }
      return alert;
    },
    onSuccess: (alert) => {
      // Raising resets everything: the next alert starts clean, with its own
      // number, rather than inheriting a photo, a transcript or an answer from
      // this one.
      resetDraft();
      void qc.invalidateQueries({ queryKey: andonKeys.home });
      // The shop floor's own andon lists — the station's, the leader's, the
      // Andon tab's — all sit under this prefix and all now have one more row.
      void qc.invalidateQueries({ queryKey: ['andons'] });
      // Onto the Andon tab with the andon open over it. Not "the raise screen
      // with an alert on it": the raise is finished, and the thing on screen is
      // now a conversation somebody else is about to join. `setTab` is what
      // leaves the raise screen; the sheet opens over the tab it lands on.
      setTab('andon');
      const landing = raiseLanding(alert);
      if (landing.kind === 'alert') openAndon(landing.id);
      else showError(t.raisedCouldNotOpenT);
    },
    // The server's own words — "Choose a station before raising", "Confirm
    // your PIN" — reach the banner as they are. A kiosk whose operator cookie
    // lapsed is sent to re-confirm, the way every other write is.
    onError,
  });
}
